import {
  ArrowLeftIcon,
  ArrowRightIcon,
  BriefcaseIcon,
  CalendarDaysIcon,
  CheckCircleIcon,
  ClockIcon,
  Cog6ToothIcon,
  MoonIcon,
  RocketLaunchIcon,
  SunIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import { useEffect, useState } from 'react'
import type { CurrentSearchStage, DateFormat, OnboardingGoal, ThemeMode } from '../types'

export type OnboardingPreferences = {
  goal: OnboardingGoal | null
  currentSearchStage: CurrentSearchStage | null
  autoGhostEnabled: boolean
  autoArchiveEnabled: boolean
  dateFormat: DateFormat
  themeMode: ThemeMode
  newsletterOptIn: boolean
}

type Props = {
  isOpen: boolean
  email: string | null
  initialPreferences: OnboardingPreferences
  isSaving?: boolean
  onComplete: (preferences: OnboardingPreferences, startTour?: boolean) => Promise<void> | void
}

const goals: Array<{
  value: OnboardingGoal
  title: string
  description: string
}> = [
  {
    value: 'track_applications',
    title: 'Track active applications',
    description: 'Keep companies, status, notes, and follow-ups in one place.',
  },
  {
    value: 'improve_resume',
    title: 'Improve my resume',
    description: 'Focus on ATS feedback, keywords, and role matching.',
  },
  {
    value: 'prepare_interviews',
    title: 'Prepare for interviews',
    description: 'Use stages, reminders, and practice questions.',
  },
  {
    value: 'compare_offers',
    title: 'Compare offers',
    description: 'Track salary, currency, benefits, and decision context.',
  },
  {
    value: 'exploring',
    title: 'Just exploring',
    description: 'Get a quick feel for the workflow first.',
  },
]

const stages: Array<{
  value: CurrentSearchStage
  title: string
  description: string
}> = [
  {
    value: 'starting',
    title: 'Starting soon',
    description: 'I am getting organized before applying.',
  },
  {
    value: 'actively_applying',
    title: 'Actively applying',
    description: 'I am sending applications and need a reliable tracker.',
  },
  {
    value: 'interviewing',
    title: 'Interviewing',
    description: 'I need reminders, stages, and prep.',
  },
  {
    value: 'considering_offers',
    title: 'Considering offers',
    description: 'I want to compare compensation and tradeoffs.',
  },
  {
    value: 'paused',
    title: 'Paused for now',
    description: 'I want the workspace ready for later.',
  },
]

const dateFormats: Array<{ value: DateFormat; label: string; example: string }> = [
  { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY', example: '25/05/2026' },
  { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY', example: '05/25/2026' },
  { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD', example: '2026-05-25' },
]

export const OnboardingModal = ({
  isOpen,
  email,
  initialPreferences,
  isSaving,
  onComplete,
}: Props) => {
  const [step, setStep] = useState(0)
  const [preferences, setPreferences] = useState<OnboardingPreferences>(initialPreferences)

  useEffect(() => {
    if (!isOpen) return
    const root = document.documentElement
    if (preferences.themeMode === 'dark') {
      root.classList.add('dark')
    } else if (preferences.themeMode === 'light') {
      root.classList.remove('dark')
    }
  }, [isOpen, preferences.themeMode])

  if (!isOpen) return null

  const complete = async (startTour = false) => {
    await onComplete(preferences, startTour)
  }

  const updatePreference = <Key extends keyof OnboardingPreferences>(
    key: Key,
    value: OnboardingPreferences[Key]
  ) => {
    setPreferences((current) => ({ ...current, [key]: value }))
  }

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-app-ink/35 dark:bg-black/60 px-4 py-6 backdrop-blur-sm">
      <div className="relative max-h-[92vh] w-full max-w-3xl overflow-hidden rounded-2xl border border-app-line bg-app-surface shadow-2xl">
        <button
          type="button"
          onClick={() => void complete(false)}
          disabled={isSaving}
          className="absolute right-4 top-4 z-10 rounded-full p-1.5 text-app-muted transition hover:bg-app-subtle hover:text-app-ink disabled:opacity-50"
          aria-label="Skip onboarding"
        >
          <XMarkIcon className="h-5 w-5" />
        </button>

        <div className="grid max-h-[92vh] overflow-y-auto md:grid-cols-[0.9fr_1.1fr]">
          <div className="relative overflow-hidden bg-app-highlight p-6 text-app-ink">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_10%,rgba(14,165,233,0.32),transparent_34%),radial-gradient(circle_at_90%_70%,rgba(16,185,129,0.2),transparent_35%)] hidden" />
            <div className="relative flex h-full min-h-64 flex-col justify-between">
              <div>
                <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-app-surface/10 text-app-accent ring-1 ring-white/15">
                  <RocketLaunchIcon className="h-6 w-6" />
                </div>
                <p className="mt-6 text-xs font-semibold uppercase tracking-[0.24em] text-app-accent">
                  First-run setup
                </p>
                <h2 className="mt-3 text-3xl font-bold leading-tight font-display">
                  Tune JobTrackr to your search.
                </h2>
                <p className="mt-4 text-sm leading-6 text-app-muted">
                  A few quick preferences, then the dashboard can suggest the right next actions without blocking you.
                </p>
              </div>
              <div className="mt-8 text-xs text-app-muted">
                Signed in as <span className="font-semibold text-app-muted">{email || 'your account'}</span>
              </div>
            </div>
          </div>

          <div className="p-6">
            <div className="mb-6 flex gap-2">
              {[0, 1, 2, 3].map((item) => (
                <span
                  key={item}
                  className={`h-1.5 flex-1 rounded-full ${item <= step ? 'bg-app-primary' : 'bg-app-subtle'}`}
                />
              ))}
            </div>

            {step === 0 && (
              <div className="space-y-5">
                <div>
                  <h3 className="text-xl font-bold text-app-ink font-display">What are you here to do first?</h3>
                  <p className="mt-2 text-sm text-app-muted">
                    Pick the closest match. You can use everything either way.
                  </p>
                </div>
                <div className="grid gap-3">
                  {goals.map((item) => (
                    <button
                      key={item.value}
                      type="button"
                      onClick={() => updatePreference('goal', item.value)}
                      className={`rounded-xl border p-4 text-left transition ${
                        preferences.goal === item.value
                          ? 'border-app-accent bg-app-highlight ring-2 ring-app-accent/20'
                          : 'border-app-line hover:border-app-line'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <CheckCircleIcon className={`mt-0.5 h-5 w-5 ${preferences.goal === item.value ? 'text-app-accent' : 'text-app-muted'}`} />
                        <div>
                          <p className="font-semibold text-app-ink">{item.title}</p>
                          <p className="mt-1 text-sm text-app-muted">{item.description}</p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {step === 1 && (
              <div className="space-y-5">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-emerald-100 text-emerald-700 dark:bg-app-highlight dark:text-app-accent">
                  <BriefcaseIcon className="h-7 w-7" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-app-ink font-display">Where are you in the search?</h3>
                  <p className="mt-2 text-sm leading-6 text-app-muted">
                    This helps tailor the prompts you see after setup.
                  </p>
                </div>
                <div className="grid gap-3">
                  {stages.map((item) => (
                    <button
                      key={item.value}
                      type="button"
                      onClick={() => updatePreference('currentSearchStage', item.value)}
                      className={`rounded-xl border p-4 text-left transition ${
                        preferences.currentSearchStage === item.value
                          ? 'border-app-accent bg-app-highlight ring-2 ring-app-accent/20'
                          : 'border-app-line hover:border-emerald-300 dark:hover:border-app-accent'
                      }`}
                    >
                      <p className="font-semibold text-app-ink">{item.title}</p>
                      <p className="mt-1 text-sm text-app-muted">{item.description}</p>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-5">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-app-highlight text-app-accent">
                  <Cog6ToothIcon className="h-7 w-7" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-app-ink font-display">Set your tracking preferences</h3>
                  <p className="mt-2 text-sm leading-6 text-app-muted">
                    These map directly to your app settings and can be changed later.
                  </p>
                </div>

                <div className="space-y-3">
                  <label className="flex items-start gap-3 rounded-xl border border-app-line p-4">
                    <input
                      type="checkbox"
                      checked={preferences.autoGhostEnabled}
                      onChange={(event) => updatePreference('autoGhostEnabled', event.target.checked)}
                      className="mt-1 h-4 w-4 border-app-line text-app-accent focus:ring-app-accent dark:bg-app-surface rounded-lg"
                    />
                    <span>
                      <span className="flex items-center gap-2 font-semibold text-app-ink">
                        <ClockIcon className="h-4 w-4 text-app-accent" />
                        Auto-ghost stale applications
                      </span>
                      <span className="mt-1 block text-sm text-app-muted">
                        Flag silent applications automatically so your dashboard stays honest.
                      </span>
                    </span>
                  </label>

                  <label className="flex items-start gap-3 rounded-xl border border-app-line p-4">
                    <input
                      type="checkbox"
                      checked={preferences.autoArchiveEnabled}
                      onChange={(event) => updatePreference('autoArchiveEnabled', event.target.checked)}
                      className="mt-1 h-4 w-4 border-app-line text-app-accent focus:ring-app-accent dark:bg-app-surface rounded-lg"
                    />
                    <span>
                      <span className="flex items-center gap-2 font-semibold text-app-ink">
                        <CalendarDaysIcon className="h-4 w-4 text-app-accent" />
                        Auto-archive old applications
                      </span>
                      <span className="mt-1 block text-sm text-app-muted">
                        Keep old closed-out applications from crowding active work.
                      </span>
                    </span>
                  </label>
                </div>

                <div>
                  <p className="mb-2 text-sm font-semibold text-app-ink">Date format</p>
                  <div className="grid gap-2 sm:grid-cols-3">
                    {dateFormats.map((item) => (
                      <button
                        key={item.value}
                        type="button"
                        onClick={() => updatePreference('dateFormat', item.value)}
                        className={`rounded-xl border px-3 py-3 text-left transition ${
                          preferences.dateFormat === item.value
                            ? 'border-app-accent bg-app-highlight ring-2 ring-app-accent/20'
                            : 'border-app-line hover:border-app-line'
                        }`}
                      >
                        <span className="block text-sm font-semibold text-app-ink">{item.label}</span>
                        <span className="mt-1 block text-xs text-app-muted">{item.example}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <p className="mb-2 text-sm font-semibold text-app-ink">Appearance</p>
                  <div className="grid gap-2 sm:grid-cols-2">
                    <button
                      type="button"
                      onClick={() => updatePreference('themeMode', 'light')}
                      className={`flex items-center gap-3 rounded-xl border px-3 py-3 text-left transition ${
                        preferences.themeMode === 'light'
                          ? 'border-app-accent bg-app-highlight ring-2 ring-app-accent/20'
                          : 'border-app-line hover:border-app-line'
                      }`}
                    >
                      <SunIcon className={`h-5 w-5 ${preferences.themeMode === 'light' ? 'text-app-accent' : 'text-app-muted'}`} />
                      <span>
                        <span className="block text-sm font-semibold text-app-ink">Light</span>
                        <span className="mt-1 block text-xs text-app-muted">Bright background</span>
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={() => updatePreference('themeMode', 'dark')}
                      className={`flex items-center gap-3 rounded-xl border px-3 py-3 text-left transition ${
                        preferences.themeMode === 'dark'
                          ? 'border-app-accent bg-app-highlight ring-2 ring-app-accent/20'
                          : 'border-app-line hover:border-app-line'
                      }`}
                    >
                      <MoonIcon className={`h-5 w-5 ${preferences.themeMode === 'dark' ? 'text-app-accent' : 'text-app-muted'}`} />
                      <span>
                        <span className="block text-sm font-semibold text-app-ink">Dark</span>
                        <span className="mt-1 block text-xs text-app-muted">Easier on the eyes</span>
                      </span>
                    </button>
                  </div>
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="space-y-5">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-amber-100 text-amber-700 dark:bg-app-highlight dark:text-app-accent">
                  <RocketLaunchIcon className="h-7 w-7" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-app-ink font-display">You are ready to start</h3>
                  <p className="mt-2 text-sm leading-6 text-app-muted">
                    We will show contextual cards on the dashboard for adding a job, uploading a resume, installing the extension, and taking the product tour.
                  </p>
                </div>
                <div className="border border-app-line bg-app-subtle p-4 text-sm text-app-muted rounded-2xl shadow-app-card">
                  Nothing here starts a long workflow. Your dashboard is next.
                </div>
                <label className="flex items-start gap-3 rounded-xl border border-app-line p-4">
                  <input
                    type="checkbox"
                    checked={preferences.newsletterOptIn}
                    onChange={(event) => updatePreference('newsletterOptIn', event.target.checked)}
                    className="mt-1 h-4 w-4 border-app-line text-app-accent focus:ring-app-accent dark:bg-app-surface rounded-lg"
                  />
                  <span>
                    <span className="block font-semibold text-app-ink">
                      Send me the JobTrackr newsletter
                    </span>
                    <span className="mt-1 block text-sm text-app-muted">
                      Occasional product updates and job search tips to{' '}
                      <span className="font-semibold text-app-ink">{email || 'your account email'}</span>. Unsubscribe any time.
                    </span>
                  </span>
                </label>
              </div>
            )}

            <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-between">
              <button
                type="button"
                onClick={() => step === 0 ? void complete(false) : setStep((current) => current - 1)}
                disabled={isSaving}
                className="inline-flex items-center justify-center gap-2 border border-app-line px-4 py-3 text-sm font-semibold text-app-ink transition hover:bg-app-subtle disabled:opacity-50 rounded-lg"
              >
                {step === 0 ? (
                  'Skip setup'
                ) : (
                  <>
                    <ArrowLeftIcon className="h-4 w-4" />
                    Back
                  </>
                )}
              </button>
              {step < 3 ? (
                <button
                  type="button"
                  onClick={() => setStep((current) => current + 1)}
                  className="inline-flex items-center justify-center gap-2 bg-app-primary px-5 py-3 text-sm font-semibold text-white transition hover:bg-app-primary-hover rounded-lg"
                >
                  Continue
                  <ArrowRightIcon className="h-4 w-4" />
                </button>
              ) : (
                <div className="flex flex-col gap-3 sm:flex-row">
                  <button
                    type="button"
                    onClick={() => void complete(false)}
                    disabled={isSaving}
                    className="border border-app-line px-4 py-3 text-sm font-semibold text-app-ink transition hover:bg-app-subtle disabled:opacity-50 rounded-lg"
                  >
                    Go to dashboard
                  </button>
                  <button
                    type="button"
                    onClick={() => void complete(true)}
                    disabled={isSaving}
                    className="inline-flex items-center justify-center gap-2 bg-app-primary px-5 py-3 text-sm font-semibold text-white transition hover:bg-app-primary-hover disabled:opacity-60 rounded-lg"
                  >
                    Show me around
                    <ArrowRightIcon className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
